import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

export class StringCursor extends StringModule {
  protected enterObjectsMap: Map<string, StringObject> = new Map<string, StringObject>();
  protected enterObjects: Array<StringObject> = new Array<StringObject>();
  cursor: any;
  cursorContent: any;
  overCount: number = 0;

  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "cursor";

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.cursor = document.querySelector("[string-cursor],[data-string-cursor]") as HTMLElement;
    this.cursorContent = document.querySelector(
      "[string-cursor-content],[data-string-cursor-content]"
    ) as HTMLElement;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "target-disable",
        type: "boolean",
        fallback: this.settings["target-disable"],
      },
      {
        key: "target-style-disable",
        type: "boolean",
        fallback: this.settings["target-style-disable"],
      },
      {
        key: "target-class",
        type: "string",
        fallback: this.settings["target-class"],
      },
      {
        key: "cursor-class",
        type: "string",
        fallback: this.settings["cursor-class"],
      },
      {
        key: "alignment",
        type: { type: "enum", values: ["start", "center", "end"] },
        fallback: this.settings["alignment"],
      },
      {
        key: "lerp",
        type: "number",
        fallback: this.settings["lerp"],
        transform: (value: number) => {
          return this.tools.adaptiveLerp.process({
            value,
            inMin: 0.1,
            inMax: 1.0,
            outMin: 0.05,
            outMax: 0.65,
          });
        },
      },
    ];
  }

  initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("mouse-x", 0);
    object.setProperty("mouse-y", 0);
    object.setProperty("mouse-pixel-x", 0);
    object.setProperty("mouse-pixel-y", 0);
    object.setProperty("is-mouse-over", false);
    object.setProperty("is-mouse-move", false);
  }

  onFrame(data: StringData): void {
    this.objects.forEach((object) => {
      const isOver = object.getProperty<boolean>("is-mouse-over");
      const isDisabled = object.getProperty<boolean>("cursor-target-disable");
      const lerpFactor = object.getProperty<number>("lerp") ?? 0.15;

      if (isOver && !isDisabled) {
        const rect = object.htmlElement.getBoundingClientRect();
        const cursorX = this.data.cursor.targetX;
        const cursorY = this.data.cursor.targetY;
        const elementX = cursorX - rect.left;
        const elementY = cursorY - rect.top;

        let px = object.getProperty<number>("mouse-pixel-x") ?? 0;
        let py = object.getProperty<number>("mouse-pixel-y") ?? 0;

        const dx = px - elementX;
        const dy = py - elementY;
        const distSquared = dx * dx + dy * dy;

        if (distSquared > 0.0001) {
          const isMoving = object.getProperty<boolean>("is-mouse-move") ?? false;
          if (!isMoving) {
            object.setProperty("is-mouse-move", true);
            object.setProperty("mouse-pixel-x", elementX);
            object.setProperty("mouse-pixel-y", elementY);
            object.setProperty("mouse-x", elementX);
            object.setProperty("mouse-y", elementY);
            px = elementX;
            py = elementY;
            this.events.emit(`cursor:start:${object.id}`, null);
          }

          const lerpedX = this.tools.lerp.process({
            from: px,
            to: elementX,
            progress: lerpFactor,
          });
          const lerpedY = this.tools.lerp.process({
            from: py,
            to: elementY,
            progress: lerpFactor,
          });

          const updatedX = px + lerpedX;
          const updatedY = py + lerpedY;

          object.setProperty("mouse-pixel-x", updatedX);
          object.setProperty("mouse-pixel-y", updatedY);

          const alignment = object.getProperty<string>("alignment") ?? "center";
          const offsetX = this.calculateOffset(alignment, updatedX, rect.width);
          const offsetY = this.calculateOffset(alignment, updatedY, rect.height);

          object.setProperty("mouse-x", offsetX);
          object.setProperty("mouse-y", offsetY);

          this.setMouseCoordinates(object, offsetX, offsetY);

          this.events.emit(`cursor:move:${object.id}`, {
            x: offsetX,
            y: offsetY,
          });
          this.events.emit(`cursor:pixel:${object.id}`, {
            x: updatedX,
            y: updatedY,
          });
        } else {
          object.setProperty("mouse-pixel-x", elementX);
          object.setProperty("mouse-pixel-y", elementY);
          this.events.emit(`cursor:end:${object.id}`, null);
        }
      } else {
        const mouseX = object.getProperty<number>("mouse-x") ?? 0;
        const mouseY = object.getProperty<number>("mouse-y") ?? 0;
        if (mouseX !== 0 || mouseY !== 0) {
          object.setProperty("is-mouse-move", false);

          const rect = object.htmlElement.getBoundingClientRect();
          const halfWidth = object.getProperty<number>("half-width") ?? rect.width / 2;
          const halfHeight = object.getProperty<number>("half-height") ?? rect.height / 2;

          const targetX = this.calculateOffset("center", halfWidth, rect.width);
          const targetY = this.calculateOffset("center", halfHeight, rect.height);

          const newMouseX =
            mouseX +
            this.tools.lerp.process({
              from: mouseX,
              to: targetX,
              progress: lerpFactor,
            });
          const newMouseY =
            mouseY +
            this.tools.lerp.process({
              from: mouseY,
              to: targetY,
              progress: lerpFactor,
            });

          object.setProperty("mouse-x", newMouseX);
          object.setProperty("mouse-y", newMouseY);

          if (Math.abs(newMouseX) < 0.001 && Math.abs(newMouseY) < 0.001) {
            object.setProperty("mouse-x", 0);
            object.setProperty("mouse-y", 0);
            object.setProperty("mouse-pixel-x", 0);
            object.setProperty("mouse-pixel-y", 0);
          }

          this.setMouseCoordinates(object, newMouseX, newMouseY);
        }
      }
    });

    const { stepX, stepY, smoothedX, smoothedY } = this.data.cursor;
    if (stepX !== 0 || stepY !== 0) {
      this.events.emit("cursor", {
        stepX,
        stepY,
        x: smoothedX,
        y: smoothedY,
      });

      this.cursor.style.setProperty("--x", smoothedX.toString());
      this.cursor.style.setProperty("--y", smoothedY.toString());
      this.cursor.style.setProperty("--x-lerp", stepX.toString());
      this.cursor.style.setProperty("--y-lerp", stepY.toString());
    }
  }

  onObjectConnected(object: StringObject) {
    const element = object.htmlElement;
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    object.setProperty("timeoutId", timeoutId);

    object.setProperty("mouseleave", () => {
      this.onMouseLeave(object);
    });
    object.setProperty("mouseenter", () => {
      this.onMouseEnter(object);
    });

    object.setProperty("onEnterEvent", this.onEnterObject.bind(this));
    object.events.on("enter", object.getProperty<(object: StringObject) => void>("onEnterEvent"));
    object.setProperty("onLeaveEvent", this.onLeaveObject.bind(this));
    object.events.on("leave", object.getProperty<(object: StringObject) => void>("onLeaveEvent"));
  }

  getCursorClass(object: StringObject) {
    const value = object.getProperty<string>("cursor-class");
    return value != null && value.length > 0 ? value : null;
  }

  onMouseEnter(object: StringObject) {
    this.overCount++;
    object.setProperty("is-mouse-over", true);

    const cursorClass = this.getCursorClass(object);

    if (this.cursor) {
      if (cursorClass) {
        this.cursor.classList.add(cursorClass);
      }
      this.cursor.classList.add("-showing");

      object.setProperty(
        "timeoutId",
        setTimeout(() => {
          this.cursor.classList.remove("-showing");
          this.cursor.classList.add("-show");
        }, 1200)
      );
    }

    object.htmlElement.addEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  onMouseLeave(object: StringObject) {
    this.overCount--;
    object.setProperty("is-mouse-over", false);

    if (object.getProperty("timeoutId")) {
      clearTimeout(object.getProperty("timeoutId"));
      object.setProperty("timeoutId", null);
    }

    const cursorClass = this.getCursorClass(object);
    if (this.cursor) {
      if (cursorClass) {
        this.cursor.classList.remove(cursorClass);
      }

      this.cursor.classList.remove("-showing");
      this.cursor.classList.remove("-show");
    }
    object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  private onEnterObject(object: StringObject) {
    object.htmlElement.addEventListener("mouseenter", object.getProperty("mouseenter"));
  }
  private onLeaveObject(object: StringObject) {
    object.htmlElement.removeEventListener("mouseenter", object.getProperty("mouseenter"));
    object.htmlElement.removeEventListener("mouseleave", object.getProperty("mouseleave"));
  }

  private setMouseCoordinates(object: StringObject, x: number, y: number) {
    if (!object.getProperty("cursor-target-style-disable")) {
      this.applyToElementAndConnects(object, (el) => {
        el.style.setProperty("--x", x.toFixed(2));
        el.style.setProperty("--y", y.toFixed(2));
      });
    }
  }

  private calculateOffset(alignment: string, mousePos: number, size: number): number {
    switch (alignment) {
      case "start":
        return mousePos / size;
      case "end":
        return (mousePos - size) / size;
      case "center":
      default:
        return (mousePos - size / 2) / (size / 2);
    }
  }
}
